import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// Icons
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ShareOutlinedIcon from "@material-ui/icons/ShareOutlined";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import CampaignOutlinedIcon from "@material-ui/icons/CastConnectedOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import BoltOutlinedIcon from "@material-ui/icons/OfflineBoltOutlined";

import { workforceApi } from "src/actions/workforce.actions";

// ─── Design tokens (shared with the host dashboard) ──────────────────────────
const TOKENS = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  border: "#EEF0F5",
  borderStrong: "#E5E7EB",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

// Each agent gets a stable colour + icon, keyed by its backend `code`.
const AGENT_THEME = {
  tim: { bg: "#F0F9FF", fg: "#0EA5E9", Icon: PeopleOutlineIcon },
  david: { bg: "#ECFDF5", fg: "#10B981", Icon: MailOutlineIcon },
  lora: { bg: "#FAF5FF", fg: "#A21CAF", Icon: ShareOutlinedIcon },
  milli: { bg: "#F1F5F9", fg: "#64748B", Icon: PhoneOutlinedIcon },
};
const agentTheme = (code) => AGENT_THEME[code] || AGENT_THEME.milli;

const STATUS_STYLE = {
  active: { bg: "#ECFDF5", fg: "#10B981", label: "Active" },
  working: { bg: "#FFFBEB", fg: "#D97706", label: "Working" },
  paused: { bg: "#F1F5F9", fg: "#64748B", label: "Paused" },
};

const STAT_THEME = {
  leads: { bg: "#F0F9FF", fg: "#0EA5E9", Icon: PeopleOutlineIcon },
  qualified: { bg: "#F2F2F2", fg: "#222222", Icon: EmojiObjectsOutlinedIcon },
  emails: { bg: "#ECFDF5", fg: "#10B981", Icon: SendOutlinedIcon },
  posts: { bg: "#FAF5FF", fg: "#A21CAF", Icon: CampaignOutlinedIcon },
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: TOKENS.bg,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
  },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    gap: theme.spacing(2),
    color: TOKENS.textSecondary,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  greeting: {
    fontSize: 28,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    lineHeight: 1.2,
    marginBottom: 6,
  },
  greetingSub: { fontSize: 14, color: TOKENS.textSecondary },
  refreshBtn: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.borderStrong}`,
    borderRadius: 10,
    width: 40,
    height: 40,
  },

  // Setup card
  setupCard: {
    background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
    borderRadius: 20,
    padding: theme.spacing(3.5),
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    marginBottom: theme.spacing(3),
  },
  ringWrap: { position: "relative", width: 120, height: 120, flexShrink: 0 },
  ringLabel: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  ringPct: { fontSize: 26, fontWeight: 700, lineHeight: 1 },
  ringCap: { fontSize: 10, color: "rgba(255,255,255,0.6)", marginTop: 2 },
  setupBody: { flex: 1, minWidth: 240 },
  setupTitle: { fontSize: 18, fontWeight: 700, marginBottom: 2 },
  setupSub: {
    fontSize: 13,
    color: "rgba(255,255,255,0.65)",
    marginBottom: theme.spacing(2),
  },
  setupItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "5px 0",
  },
  setupItemText: { fontSize: 13.5, color: "rgba(255,255,255,0.9)", flex: 1 },
  setupItemDone: {
    color: "rgba(255,255,255,0.5)",
    textDecoration: "line-through",
  },
  setupAction: {
    color: "#A5B4FC",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
  },

  // Stat cards
  statCard: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    height: "100%",
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statLabel: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    fontWeight: 500,
    marginBottom: 4,
  },
  statValue: {
    fontSize: 22,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    lineHeight: 1.2,
  },

  sectionHeading: {
    fontSize: 18,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  manageLink: {
    fontSize: 13,
    fontWeight: 600,
    color: TOKENS.accent,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },

  // Agent card
  agentCard: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
    },
  },
  agentTop: { display: "flex", alignItems: "center", gap: 12 },
  agentName: { fontSize: 15, fontWeight: 700, color: TOKENS.textPrimary },
  agentRole: { fontSize: 12.5, color: TOKENS.textSecondary },
  statusBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 999,
  },
  agentAction: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${TOKENS.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  lastAction: {
    fontSize: 12.5,
    color: TOKENS.textSecondary,
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  lastActionText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pendingPill: {
    fontSize: 11,
    fontWeight: 700,
    color: "#B45309",
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: 999,
    padding: "2px 9px",
    whiteSpace: "nowrap",
  },

  // Side panels
  sideCard: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    padding: theme.spacing(3),
  },
  sideTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sideSub: {
    fontSize: 12.5,
    color: TOKENS.textSecondary,
    marginBottom: theme.spacing(2),
  },

  // Needs attention rows
  attnRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 0",
    borderBottom: `1px solid ${TOKENS.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  attnIcon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  attnBody: { flex: 1, minWidth: 0 },
  attnAgent: { fontSize: 12, fontWeight: 700, color: TOKENS.accent },
  attnSummary: { fontSize: 13, color: TOKENS.textPrimary, marginTop: 1 },
  approveBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12.5,
    borderRadius: 9,
    backgroundColor: TOKENS.accent,
    color: "#FFF",
    boxShadow: "none",
    padding: "4px 14px",
    "&:hover": { backgroundColor: "#000000", boxShadow: "none" },
  },
  resolveLink: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#D97706",
    cursor: "pointer",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },

  // Activity feed
  feedRow: { display: "flex", gap: 12, padding: "10px 0" },
  feedDot: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  feedText: { fontSize: 13, color: TOKENS.textPrimary, lineHeight: 1.4 },
  feedTime: { fontSize: 11.5, color: TOKENS.textMuted, marginTop: 2 },

  emptyState: {
    textAlign: "center",
    padding: "28px 0",
    color: TOKENS.textMuted,
    fontSize: 13,
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────
const timeAgo = (iso) => {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

// SVG progress ring for the setup card.
const ProgressRing = ({ percent }) => {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width="120" height="120" viewBox="0 0 120 120">
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="10"
      />
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke="#A5B4FC"
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform="rotate(-90 60 60)"
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
    </svg>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
const WorkforceDashboard = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [agents, setAgents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [approvingId, setApprovingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [summaryData, agentsData, activityData] = await Promise.all([
        workforceApi.getDashboard(),
        workforceApi.getAgents(),
        workforceApi.getActivities(),
      ]);
      setSummary(summaryData);
      setAgents(agentsData || []);
      setActivities((activityData || []).slice(0, 8));
    } catch (err) {
      // Leave whatever loaded; the empty states cover the rest.
      // eslint-disable-next-line no-console
      console.error("Failed to load workforce dashboard", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id) => {
    setApprovingId(id);
    try {
      await workforceApi.approveActivity(id);
      await load();
    } finally {
      setApprovingId(null);
    }
  };

  const handleToggle = async (e, agent) => {
    e.stopPropagation();
    await workforceApi.toggleAgent(agent.id);
    await load();
  };

  if (loading) {
    return (
      <Box className={classes.page}>
        <Box className={classes.loadingWrap}>
          <CircularProgress size={32} style={{ color: TOKENS.accent }} />
          <Typography>Loading your workforce…</Typography>
        </Box>
      </Box>
    );
  }

  const setup = summary?.setup || { percent: 0, items: [] };
  const stats = summary?.stats || {};
  const needsAttention = summary?.needs_attention || [];

  const statCards = [
    { key: "leads", label: "Leads captured", value: stats.leads_captured ?? 0 },
    {
      key: "qualified",
      label: "Qualified leads",
      value: stats.qualified_leads ?? 0,
    },
    { key: "emails", label: "Emails sent", value: stats.emails_sent ?? 0 },
    {
      key: "posts",
      label: "Posts published",
      value: stats.posts_published ?? 0,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Tubayo Host | AI Workforce</title>
      </Helmet>

      <Box className={classes.page}>
        <Container maxWidth={false} style={{ maxWidth: 1440 }}>
          {/* Header */}
          <Box className={classes.header}>
            <Box>
              <Typography className={classes.greeting}>
                Your AI Workforce
              </Typography>
              <Typography className={classes.greetingSub}>
                Your agents are working in the background. Here's where things
                stand.
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton className={classes.refreshBtn} onClick={load}>
                <RefreshIcon
                  style={{ fontSize: 20, color: TOKENS.textSecondary }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Setup checklist — only while incomplete */}
          {setup.percent < 100 && (
            <Box className={classes.setupCard}>
              <Box className={classes.ringWrap}>
                <ProgressRing percent={setup.percent} />
                <Box className={classes.ringLabel}>
                  <span className={classes.ringPct}>{setup.percent}%</span>
                  <span className={classes.ringCap}>set up</span>
                </Box>
              </Box>
              <Box className={classes.setupBody}>
                <Typography className={classes.setupTitle}>
                  Finish setting up your team
                </Typography>
                <Typography className={classes.setupSub}>
                  A couple of steps left before your agents are at full
                  strength.
                </Typography>
                {setup.items.map((item) => (
                  <Box key={item.key} className={classes.setupItem}>
                    {item.done ? (
                      <CheckCircleOutlineIcon
                        style={{ fontSize: 20, color: "#34D399" }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        style={{ fontSize: 20, color: "rgba(255,255,255,0.4)" }}
                      />
                    )}
                    <span
                      className={`${classes.setupItemText} ${
                        item.done ? classes.setupItemDone : ""
                      }`}
                    >
                      {item.label}
                    </span>
                    {!item.done && item.action && (
                      <a
                        className={classes.setupAction}
                        href={item.action}
                        rel="noreferrer"
                      >
                        Set up →
                      </a>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Quick stats — past 7 days */}
          <Grid container spacing={3}>
            {statCards.map((s) => {
              const theme = STAT_THEME[s.key];
              const Icon = theme.Icon;
              return (
                <Grid key={s.key} item xs={12} sm={6} md={3}>
                  <Box className={classes.statCard}>
                    <Box
                      className={classes.statIcon}
                      style={{ backgroundColor: theme.bg, color: theme.fg }}
                    >
                      <Icon />
                    </Box>
                    <Box>
                      <Typography className={classes.statLabel}>
                        {s.label}
                      </Typography>
                      <Typography className={classes.statValue}>
                        {s.value}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Team + side panels */}
          <Grid container spacing={3} style={{ marginTop: 4 }}>
            {/* Left: the team */}
            <Grid item xs={12} md={8}>
              <Typography className={classes.sectionHeading}>
                Your team
                <span
                  className={classes.manageLink}
                  onClick={() => navigate("/app/workforce/agents")}
                >
                  Manage agents <ArrowForwardIcon style={{ fontSize: 16 }} />
                </span>
              </Typography>

              {agents.length === 0 ? (
                <Box className={classes.sideCard}>
                  <Box className={classes.emptyState}>
                    No agents activated yet. Add one to get started.
                  </Box>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {agents.map((agent) => {
                    const def = agent.definition || {};
                    const theme = agentTheme(def.code);
                    const AgentIcon = theme.Icon;
                    const status =
                      STATUS_STYLE[agent.status] ||
                      (agent.is_active
                        ? STATUS_STYLE.active
                        : STATUS_STYLE.paused);
                    return (
                      <Grid key={agent.id} item xs={12} sm={6}>
                        <Box
                          className={classes.agentCard}
                          onClick={() =>
                            navigate(`/app/workforce/agents/${agent.id}`)
                          }
                        >
                          <Box className={classes.agentTop}>
                            <Box
                              className={classes.statIcon}
                              style={{
                                backgroundColor: theme.bg,
                                color: theme.fg,
                              }}
                            >
                              <AgentIcon />
                            </Box>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Typography className={classes.agentName}>
                                {def.name}
                              </Typography>
                              <Typography className={classes.agentRole}>
                                {def.role}
                              </Typography>
                            </Box>
                            <span
                              className={classes.statusBadge}
                              style={{
                                backgroundColor: status.bg,
                                color: status.fg,
                              }}
                            >
                              {status.label}
                            </span>
                          </Box>

                          <Box className={classes.agentAction}>
                            <span className={classes.lastAction}>
                              <BoltOutlinedIcon
                                style={{
                                  fontSize: 15,
                                  color: TOKENS.textMuted,
                                }}
                              />
                              <span className={classes.lastActionText}>
                                {agent.last_action || "Waiting for next task"}
                              </span>
                            </span>
                            <Box
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              {agent.pending_approvals > 0 && (
                                <span className={classes.pendingPill}>
                                  {agent.pending_approvals} to review
                                </span>
                              )}
                              <Tooltip
                                title={agent.is_active ? "Pause" : "Resume"}
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggle(e, agent)}
                                >
                                  {agent.is_active ? (
                                    <PauseCircleOutlineIcon
                                      style={{
                                        fontSize: 20,
                                        color: TOKENS.textMuted,
                                      }}
                                    />
                                  ) : (
                                    <PlayCircleOutlineIcon
                                      style={{ fontSize: 20, color: "#10B981" }}
                                    />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Grid>

            {/* Right: needs attention + activity */}
            <Grid item xs={12} md={4}>
              <Typography className={classes.sectionHeading}>
                Needs attention
              </Typography>
              <Box className={classes.sideCard} style={{ marginBottom: 24 }}>
                {needsAttention.length === 0 ? (
                  <Box className={classes.emptyState}>
                    You're all caught up. Nothing to review.
                  </Box>
                ) : (
                  needsAttention.map((item, i) => {
                    const isApproval = item.type === "approval";
                    return (
                      <Box
                        key={item.id || `issue-${i}`}
                        className={classes.attnRow}
                      >
                        <Box
                          className={classes.attnIcon}
                          style={{
                            backgroundColor: isApproval ? "#F2F2F2" : "#FFF7ED",
                            color: isApproval ? "#222222" : "#EA580C",
                          }}
                        >
                          {isApproval ? (
                            <SendOutlinedIcon style={{ fontSize: 17 }} />
                          ) : (
                            <WarningRoundedIcon style={{ fontSize: 17 }} />
                          )}
                        </Box>
                        <Box className={classes.attnBody}>
                          {isApproval && (
                            <Typography className={classes.attnAgent}>
                              {item.agent}
                            </Typography>
                          )}
                          <Typography className={classes.attnSummary}>
                            {item.summary}
                          </Typography>
                        </Box>
                        {isApproval ? (
                          <Button
                            className={classes.approveBtn}
                            variant="contained"
                            disableElevation
                            disabled={approvingId === item.id}
                            onClick={() => handleApprove(item.id)}
                          >
                            {approvingId === item.id ? "…" : "Approve"}
                          </Button>
                        ) : (
                          item.action && (
                            <a
                              className={classes.resolveLink}
                              href={item.action}
                            >
                              Resolve →
                            </a>
                          )
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>

              <Typography className={classes.sideTitle}>
                <TrendingUpIcon
                  style={{ fontSize: 19, color: TOKENS.accent }}
                />
                Recent activity
              </Typography>
              <Box className={classes.sideCard} style={{ marginTop: 12 }}>
                {activities.length === 0 ? (
                  <Box className={classes.emptyState}>
                    No activity yet. Your agents will log their work here.
                  </Box>
                ) : (
                  activities.map((act) => {
                    const theme = agentTheme(
                      (act.agent_name || "").toLowerCase(),
                    );
                    const ActIcon = theme.Icon;
                    return (
                      <Box key={act.id} className={classes.feedRow}>
                        <Box
                          className={classes.feedDot}
                          style={{ backgroundColor: theme.bg, color: theme.fg }}
                        >
                          <ActIcon style={{ fontSize: 16 }} />
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography className={classes.feedText}>
                            <b>{act.agent_name}</b> {act.summary}
                          </Typography>
                          <Typography className={classes.feedTime}>
                            {timeAgo(act.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default WorkforceDashboard;
